import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { LessonHookResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface HookCardPDFProps {
  lessonHook: LessonHookResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  hookContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#eef2ff",
    borderRadius: 8,
  },
  hookTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#4f46e5",
    marginBottom: 8,
  },
  description: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.4,
    marginBottom: 8,
  },
});

const HookCardDocument = ({
  lessonHook,
  customizationSettings,
}: HookCardPDFProps) => {
  const { t } = useTranslation();

  return (
    <PdfLayout
      courseName={lessonHook.courseName}
      gradeLevel={lessonHook.gradeLevel}
      topic={lessonHook.title || lessonHook.prompt || ""}
      imageUrl={lessonHook.imageUrl}
      headerTextColor="#4f46e5"
      customizationSettings={customizationSettings}
      author={lessonHook.authorName}
      createdDate={lessonHook.createdAt}
    >
      {Object.values((lessonHook.hooks as Record<string, any>) || {}).map(
        (hook, index) => (
          <View wrap={false} key={index} style={styles.hookContainer}>
            {hook.title && (
              <Text
                style={[
                  styles.hookTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.hookTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {hook.title}
              </Text>
            )}
            <MarkdownRenderer
              customStyle={{
                text: {
                  text: {
                    ...styles.description,
                    fontSize: scaleFontSize(
                      styles.description.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                },
              }}
              content={hook.description || hook.content}
            />

            {hook.expected_outcome && (
              <>
                <Text
                  style={[
                    commonStyles.sectionTitle,

                    {
                      fontSize: scaleFontSize(
                        commonStyles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("hooks.expectedOutcome")}:
                </Text>
                <MarkdownRenderer
                  customStyle={{
                    text: {
                      text: {
                        ...commonStyles.sectionContent,
                        fontSize: scaleFontSize(
                          commonStyles.sectionContent.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    },
                  }}
                  content={hook.expected_outcome}
                />
              </>
            )}

            {hook.materials_needed &&
              hook.materials_needed !== "Yok" &&
              hook.materials_needed !== "None" &&
              hook.materials_needed !== "None." && (
                <>
                  <Text
                    style={[
                      commonStyles.sectionTitle,
                      {
                        fontSize: scaleFontSize(
                          commonStyles.sectionTitle.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("hooks.materialsNeeded")}:
                  </Text>
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          fontSize: scaleFontSize(
                            commonStyles.sectionContent.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                    content={hook.materials_needed}
                  />
                </>
              )}
          </View>
        ),
      )}
    </PdfLayout>
  );
};

function HookCardPDF(props: HookCardPDFProps) {
  return <HookCardDocument {...props} />;
}

export default HookCardPDF;
